/// <amd-module name="Wit/Common/WorkItemDialogs/Components/MoveToPositionDialog" />
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { MessageCard } from "VSSUI/Components/MessageCard/MessageCard";
import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { TextField } from "VSSUI/TextField";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";

export interface IMoveToPositionDialogProps<T> {
    items: T[];
    onDismiss: () => void;
    onMoveToPosition?: (index: number) => void;
}

export function MoveToPositionDialog<T>({ items, onDismiss, onMoveToPosition }: IMoveToPositionDialogProps<T>) {
    const [inputErrorMessage, setInputErrorMessage] = React.useState<string | undefined>(undefined);
    const [operationErrorMessage, setOperationErrorMessage] = React.useState<string | undefined>(undefined);
    const [selectedIndex, setSelectedIndex] = React.useState<string>("");

    const onValueChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        setSelectedIndex(value);
		// value must be a positive integer. Options like 1e1, 1.0, 1.5, -5 should cause error
        const parsedIndex = Number(value);
        if (parsedIndex < 1 || parsedIndex > items.length || !/^[0-9]+$/.test(value)) {
            setInputErrorMessage(format(Resources.InvalidPosition, items.length));
        } else if (inputErrorMessage) {
            setInputErrorMessage(undefined);
        }
    };

    const onMoveToPositionClicked = () => {
        try {
            onMoveToPosition?.(Number(selectedIndex) - 1);
            onDismiss();
        } catch (error) {
            setOperationErrorMessage(error.message);
        }
    };

    return (
        <Dialog
            defaultActiveElement=".position-textbox"
            footerButtonProps={[
                {
                    onClick: onDismiss,
                    text: Resources.Cancel
                },
                {
                    disabled: !!inputErrorMessage || !selectedIndex,
                    onClick: onMoveToPositionClicked,
                    primary: true,
                    text: Resources.SaveChanges
                }
            ]}
            onDismiss={onDismiss}
            resizable={true}
            titleProps={{ text: Resources.MoveToPositionTitle }}
            showCloseButton={true}
        >
            <div className="flex-column rhythm-vertical-8">
                {operationErrorMessage && <MessageCard severity={MessageCardSeverity.Error}>{operationErrorMessage}</MessageCard>}
                <FormItem label={format(Resources.SelectAPositionPrompt, items.length)} error={!!inputErrorMessage} message={inputErrorMessage}>
                    <TextField inputClassName="position-textbox" inputType="number" onChange={onValueChanged} value={selectedIndex} />
                </FormItem>
            </div>
        </Dialog>
    );
}
